/**
 * =============================================================================
 * SPEQ55 RFP Pro - Keyword Selector Component
 * =============================================================================
 *
 * Component for selecting and managing keywords for RFP analysis.
 *
 * @module components/analysis/KeywordSelector
 * @author SPEQ55
 */

import { useState } from 'react';
import PropTypes from 'prop-types';

function KeywordSelector({ keywords, onChange, availableKeywords }) {
  const [newKeyword, setNewKeyword] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Determine which keywords are currently selected
  const selectedSet = new Set(keywords);

  // Split into selected and available
  const selected = keywords;
  const available = availableKeywords.filter((k) => !selectedSet.has(k));

  // Show limited items unless showAll is true
  const displayLimit = 20;
  const displayedAvailable = showAll ? available : available.slice(0, displayLimit);

  /**
   * Add a keyword to selection.
   */
  const handleAdd = (keyword) => {
    if (!selectedSet.has(keyword)) {
      onChange([...keywords, keyword]);
    }
  };

  /**
   * Remove a keyword from selection.
   */
  const handleRemove = (keyword) => {
    onChange(keywords.filter((k) => k !== keyword));
  };

  /**
   * Add a custom keyword.
   */
  const handleAddCustom = (e) => {
    if (e) e.preventDefault();
    const trimmed = newKeyword.trim();
    if (trimmed && !selectedSet.has(trimmed)) {
      onChange([...keywords, trimmed]);
      setNewKeyword('');
    }
  };

  /**
   * Handle Enter key in input.
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  /**
   * Select all available keywords.
   */
  const handleSelectAll = () => {
    onChange([...new Set([...keywords, ...availableKeywords])]);
  };

  /**
   * Clear all selections.
   */
  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-accent-700">
          Keywords ({selected.length} selected)
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            Select All
          </button>
          <span className="text-accent-300">|</span>
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs text-accent-500 hover:text-accent-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Selected keywords */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((keyword) => (
            <span
              key={keyword}
              className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 text-sm rounded-md"
            >
              {keyword}
              <button
                type="button"
                onClick={() => handleRemove(keyword)}
                className="text-primary-500 hover:text-primary-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add custom keyword */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add custom keyword..."
          className="flex-1 px-3 py-2 text-sm border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={handleAddCustom}
          disabled={!newKeyword.trim()}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>

      {/* Available keywords */}
      {available.length > 0 && (
        <div>
          <p className="text-xs text-accent-500 mb-2">Click to add:</p>
          <div className="flex flex-wrap gap-2">
            {displayedAvailable.map((keyword) => (
              <button
                key={keyword}
                type="button"
                onClick={() => handleAdd(keyword)}
                className="px-2 py-1 text-sm bg-accent-100 text-accent-600 rounded-md hover:bg-accent-200 transition-colors"
              >
                + {keyword}
              </button>
            ))}
          </div>
          {available.length > displayLimit && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-2 text-xs text-primary-600 hover:text-primary-700"
            >
              Show {available.length - displayLimit} more...
            </button>
          )}
          {showAll && available.length > displayLimit && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="mt-2 text-xs text-accent-500 hover:text-accent-700"
            >
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}

KeywordSelector.propTypes = {
  keywords: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  availableKeywords: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default KeywordSelector;
